/**
 * Refinement (R15, ADR-033) — free for everyone.
 *
 * This is the moment most people meet, because almost every senior or
 * ambiguously-titled profile escalates. So it cannot be an apology. It is the
 * only step where the person tells us something the résumé never said, and it
 * costs nothing to run — pure set arithmetic, no model call — which is why it
 * sits in front of the account wall rather than behind it.
 *
 * The part that was missing: showing WHAT CHANGED. A ranking that silently
 * reorders teaches nobody anything, and gives no reason to believe the second
 * answer more than the first. So after answering, the screen reports the
 * movement and attributes it — which activity confirmed, which ruled out, and
 * which candidate that promoted or sank. The person should be able to see their
 * own input doing work; otherwise it is a form.
 */
import { useEffect, useState } from "react";
import { buildQuestions, applyAnswers, preAnswer } from "../utils/discriminate";
import { PANEL, NAVY, TEAL, CREAM, CREAM60, CREAM30, BORDER, AMBER, GREEN } from "../utils/constants";

const pct = (n) => `${Math.round((n || 0) * 100)}%`;

function Movement({ moved }) {
  if (!moved) return <span style={{ color: CREAM30, fontSize: 11 }}>—</span>;
  const up = moved > 0;
  return (
    <span style={{ color: up ? GREEN : AMBER, fontSize: 11,
                   fontFamily: "'JetBrains Mono', monospace" }}>
      {up ? "↑" : "↓"}{Math.abs(moved)}
    </span>
  );
}

export default function Refine({ matches, dwaIndex, parsed, onResolved, onApplied }) {
  const [questions, setQuestions] = useState(null);
  const [inferred, setInferred] = useState({});
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  // Keyed on the candidate SET, not the array reference. A new array with the
  // same codes is the same question — and resetting on identity alone means any
  // unrelated re-render wipes a half-answered panel.
  const candidateKey = (matches || []).map((m) => m.code).join("|");

  useEffect(() => {
    if (!matches?.length || !dwaIndex) return;
    const qs = buildQuestions(matches, dwaIndex, { limit: 5 });
    const pre = preAnswer(qs, parsed);
    setInferred(pre.inferred);
    setQuestions(pre.ask);
    setAnswers({});
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateKey, dwaIndex]);

  if (!questions) return null;
  const answered = Object.keys(answers).length;
  const inferredCount = Object.keys(inferred).length;

  const submit = () => {
    const all = { ...inferred, ...answers };
    const ranked = applyAnswers(matches, all, dwaIndex);
    setResult(ranked);
    // Report up IMMEDIATELY, not only when someone clicks a row. An earlier
    // version fired only on click, so a person who refined and simply carried
    // on had their answers silently discarded — the ranking on screen improved
    // and nothing downstream ever heard about it.
    onApplied?.(all, ranked);
  };

  // ── After: the change ledger ──────────────────────────────────────────
  if (result) {
    const leader = result[0];
    const previousLeader = matches[0];
    const changedLeader = leader.code !== previousLeader?.code;

    return (
      <div style={{ background: PANEL, border: `1px solid ${TEAL}45`, borderRadius: 12,
                    padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: TEAL, letterSpacing: 1.2, textTransform: "uppercase",
                      fontFamily: "'JetBrains Mono', monospace", marginBottom: 10 }}>
          What your answers changed
        </div>

        <p style={{ fontSize: 14, color: CREAM, lineHeight: 1.55, margin: "0 0 6px" }}>
          {changedLeader
            ? <>The best match moved from <strong style={{ color: CREAM60 }}>{previousLeader?.title}</strong> to <strong>{leader.title}</strong>.</>
            : <><strong>{leader.title}</strong> held its position — and is now {pct(leader.confidence)} confident, up from {pct(leader.confidenceBefore)}.</>}
        </p>

        {/* Why, in the person's own answers */}
        {(leader.why.helped.length > 0 || leader.why.hurt.length > 0) && (
          <div style={{ margin: "14px 0 4px" }}>
            {leader.why.helped.map((a) => (
              <div key={a} style={{ display: "flex", gap: 9, alignItems: "flex-start",
                                    fontSize: 13, color: CREAM60, lineHeight: 1.5, marginBottom: 6 }}>
                <span style={{ color: GREEN, fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                               marginTop: 2 }}>✓</span>
                <span>You do <strong style={{ color: CREAM }}>{a.replace(/\.$/, "").toLowerCase()}</strong> — core to this role.</span>
              </div>
            ))}
            {leader.why.hurt.map((a) => (
              <div key={a} style={{ display: "flex", gap: 9, alignItems: "flex-start",
                                    fontSize: 13, color: CREAM60, lineHeight: 1.5, marginBottom: 6 }}>
                <span style={{ color: AMBER, fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                               marginTop: 2 }}>—</span>
                <span>You ruled out <strong style={{ color: CREAM }}>{a.replace(/\.$/, "").toLowerCase()}</strong>, which this role usually includes.</span>
              </div>
            ))}
          </div>
        )}

        {/* The whole field, with movement */}
        <div style={{ marginTop: 16, borderTop: `1px solid ${BORDER}`, paddingTop: 12 }}>
          {result.slice(0, 5).map((c) => {
            const isLeader = c.code === leader.code;
            return (
              <button key={c.code} onClick={() => onResolved?.(c.code, result)}
                style={{ display: "flex", width: "100%", textAlign: "left", gap: 10,
                         alignItems: "baseline", background: isLeader ? `${TEAL}12` : "transparent",
                         border: `1px solid ${isLeader ? `${TEAL}40` : "transparent"}`,
                         borderRadius: 8, padding: "9px 11px", marginBottom: 5, cursor: "pointer",
                         color: CREAM, fontSize: 13.5, fontFamily: "'Space Grotesk', sans-serif" }}>
                <span style={{ flex: 1 }}>{c.title}</span>
                <Movement moved={c.moved} />
                <span style={{ width: 82, textAlign: "right", color: CREAM30, fontSize: 11,
                               fontFamily: "'JetBrains Mono', monospace",
                               fontVariantNumeric: "tabular-nums" }}>
                  {pct(c.confidenceBefore)} → <span style={{ color: isLeader ? TEAL : CREAM60 }}>{pct(c.confidence)}</span>
                </span>
              </button>
            );
          })}
        </div>

        <p style={{ fontSize: 11.5, color: CREAM30, lineHeight: 1.6, margin: "12px 0 0" }}>
          Pick any of these if we still have it wrong — your choice wins over ours, always.
          {leader.confidence < 0.7 && " Still not certain: a few answers narrow the field, they don't settle it."}
        </p>

        <button onClick={() => setResult(null)}
          style={{ background: "none", border: `1px solid ${CREAM30}`, color: CREAM60, marginTop: 14,
                   padding: "7px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12.5 }}>
          ← Change my answers
        </button>
      </div>
    );
  }

  // ── Before: the questions ─────────────────────────────────────────────
  return (
    <div style={{ background: PANEL, border: `1px solid ${AMBER}40`, borderRadius: 12,
                  padding: 20, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: CREAM, marginBottom: 4 }}>
        Two people with your title do different work
      </div>
      <p style={{ fontSize: 12.5, color: CREAM60, margin: "0 0 4px", lineHeight: 1.55 }}>
        {questions.length === 0
          ? "We worked all of this out from your profile — nothing left to ask."
          : `${questions.length} question${questions.length === 1 ? "" : "s"} and we'll score your work, not the average.`}
      </p>

      {inferredCount > 0 && (
        <details style={{ margin: "12px 0 0" }}>
          <summary style={{ fontSize: 11.5, color: TEAL, cursor: "pointer",
                            fontFamily: "'JetBrains Mono', monospace" }}>
            {inferredCount} answered from your own profile — we didn't ask
          </summary>
          <div style={{ marginTop: 9 }}>
            {Object.keys(inferred).map((a) => (
              <div key={a} style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 12.5,
                                    color: CREAM60, lineHeight: 1.5, marginBottom: 5 }}>
                <span style={{ color: GREEN, fontSize: 11, marginTop: 1 }}>✓</span>
                <span>{a}</span>
              </div>
            ))}
            <p style={{ fontSize: 11, color: CREAM30, marginTop: 8, lineHeight: 1.55 }}>
              Read from what you already wrote. Say so below if any of it is wrong.
            </p>
          </div>
        </details>
      )}

      <div style={{ marginTop: 16 }}>
        {questions.map((q) => (
          <div key={q.id} style={{ display: "flex", gap: 12, alignItems: "center",
                                   padding: "11px 0", borderBottom: `1px dashed ${BORDER}` }}>
            <span style={{ flex: 1, fontSize: 13.5, color: CREAM, lineHeight: 1.5 }}>{q.activity}</span>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              {[["Yes", true], ["No", false]].map(([label, val]) => {
                const on = answers[q.activity] === val;
                return (
                  <button key={label}
                    onClick={() => setAnswers((a) => ({ ...a, [q.activity]: val }))}
                    style={{ background: on ? (val ? GREEN : CREAM30) : "transparent",
                             color: on ? NAVY : CREAM60,
                             border: `1px solid ${on ? (val ? GREEN : CREAM30) : CREAM30}`,
                             borderRadius: 6, padding: "5px 14px", fontSize: 12.5,
                             fontWeight: on ? 700 : 500, cursor: "pointer" }}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <button onClick={submit} disabled={answered === 0 && inferredCount === 0}
        style={{ marginTop: 16, background: answered ? TEAL : "transparent",
                 color: answered ? NAVY : CREAM60,
                 border: `1px solid ${answered ? TEAL : CREAM30}`, borderRadius: 8,
                 padding: "10px 18px", fontSize: 13.5, fontWeight: answered ? 700 : 500,
                 cursor: answered || inferredCount ? "pointer" : "default" }}>
        {answered ? `Refine the matching (${answered} answered)` : "Refine the matching"}
      </button>
    </div>
  );
}
